import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Upload, Form, Typography, Col, Progress, Button } from 'antd';
import {
  CloudUploadOutlined,
  DeleteOutlined,
  StarFilled,
  StarOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { FieldConfig } from '../../types/fieldTypes';
import { useValidator } from '../../hooks/useValidator';
import './ImageUploadField.css';

const { Text } = Typography;

interface ImageUploadFieldProps {
  field: FieldConfig;
  value: any;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  mode: 'edit' | 'view';
  formData?: Record<string, any>;
}

const toUploadFiles = (value: any): UploadFile[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, idx): UploadFile | null => {
      if (!item) return null;
      if (typeof item === 'object' && (item.url || item.thumbnailUrl)) {
        return {
          uid: String(item.id ?? `existing-${idx}`),
          name: item.alt || `photo-${idx + 1}`,
          status: 'done',
          url: item.thumbnailUrl || item.url,
          thumbUrl: item.thumbnailUrl || item.url,
        };
      }
      if (typeof item === 'object' && 'uid' in item) {
        return item as UploadFile;
      }
      if (item instanceof File) {
        return {
          uid: `local-${idx}`,
          name: item.name,
          status: 'done',
          originFileObj: item as any,
          thumbUrl: URL.createObjectURL(item),
        };
      }
      return null;
    })
    .filter(Boolean) as UploadFile[];
};

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  field,
  value,
  onChange,
  mode,
  formData = {},
}) => {
  const { t } = useTranslation();
  const { touched, validationError, handleValidate } = useValidator({
    field,
    value,
    formData,
  });
  const [fileList, setFileList] = useState<UploadFile[]>(() => toUploadFiles(value));
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    const incoming = toUploadFiles(value);
    if (incoming.length > 0) {
      setFileList(incoming);
      hydratedRef.current = true;
    }
  }, [value]);

  const propagate = (next: UploadFile[]) => {
    const payload = next.map(f => f.originFileObj || f).filter(Boolean);
    const syntheticEvent = {
      target: { name: field.name, value: payload },
    } as unknown as ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
    handleValidate(payload);
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    // Generate thumb URLs for newly added local files so previews show.
    const withThumbs = newFileList.map(f => {
      if (f.originFileObj && !f.thumbUrl) {
        return { ...f, thumbUrl: URL.createObjectURL(f.originFileObj) };
      }
      return f;
    });
    setFileList(withThumbs);
    hydratedRef.current = true;
    propagate(withThumbs);
  };

  const removeAt = (idx: number) => {
    const next = fileList.filter((_, i) => i !== idx);
    setFileList(next);
    propagate(next);
  };

  const setPrimary = (idx: number) => {
    if (idx === 0) return;
    const next = [...fileList];
    const [picked] = next.splice(idx, 1);
    next.unshift(picked);
    setFileList(next);
    propagate(next);
  };

  if (mode === 'view') {
    return (
      <Col span={field.layout?.span || 24}>
        <Form.Item label={<Text type="secondary">{field.label}</Text>}>
          <Text type="secondary">
            {Array.isArray(value)
              ? `${value.length} images uploaded`
              : t('image_upload.no_images')}
          </Text>
        </Form.Item>
      </Col>
    );
  }

  const colProps = {
    span: field.layout?.span || 24,
    offset: field.layout?.offset || 0,
    order: field.layout?.order,
  };

  const acceptedFormats =
    field.customProps?.acceptedFormats?.map(f => `.${f}`).join(',') ||
    '.jpg,.jpeg,.png,.webp,.heic';
  const maxFiles = field.customProps?.maxFiles || 15;
  const maxFileSizeMB = field.customProps?.maxFileSize || 10;
  const remaining = Math.max(0, maxFiles - fileList.length);
  const fillPct = Math.round((fileList.length / maxFiles) * 100);
  const atLimit = remaining === 0;

  return (
    <Col {...colProps} className={field.layout?.className}>
      <Form.Item
        validateStatus={validationError && touched ? 'error' : ''}
        help={
          validationError && touched ? (
            <Text type="danger">{validationError}</Text>
          ) : null
        }
      >
        <div className="iu-wrap">
          {/* Drop zone */}
          <Upload
            multiple
            accept={acceptedFormats}
            fileList={fileList}
            onChange={handleChange}
            beforeUpload={() => false}
            maxCount={maxFiles}
            showUploadList={false}
            disabled={atLimit}
          >
            <div className={`iu-drop ${atLimit ? 'iu-drop-disabled' : ''}`}>
              <div className="iu-drop-icon">
                <CloudUploadOutlined />
              </div>
              <div className="iu-drop-title">
                {atLimit
                  ? t('image_upload.limit_reached')
                  : fileList.length === 0
                    ? t('image_upload.add_photos')
                    : t('image_upload.add_more_photos')}
              </div>
              <div className="iu-drop-sub">
                {atLimit
                  ? t('image_upload.remove_to_add')
                  : `${t('image_upload.drop_or_click')} — JPG, PNG, WebP up to ${maxFileSizeMB} MB`}
              </div>
              {!atLimit && (
                <Button type="primary" className="iu-drop-btn" size="large">
                  {t('image_upload.choose_photos')}
                </Button>
              )}
            </div>
          </Upload>

          {/* Counter + progress */}
          <div className="iu-meta">
            <div className="iu-meta-row">
              <span className="iu-meta-count">
                <PictureOutlined /> {fileList.length} of {maxFiles} photos
              </span>
              {fileList.length > 0 && (
                <span className="iu-meta-hint">
                  Tap a photo to set it as primary · drag the X to remove
                </span>
              )}
            </div>
            <Progress
              percent={fillPct}
              showInfo={false}
              strokeColor="#FE5A1D"
              trailColor="#f0f0f0"
              size="small"
            />
          </div>

          {/* Thumbnail grid */}
          {fileList.length > 0 && (
            <div className="iu-grid">
              {fileList.map((f, idx) => {
                const src = f.thumbUrl || f.url;
                const isPrimary = idx === 0;
                return (
                  <div
                    key={f.uid}
                    className={`iu-thumb ${isPrimary ? 'iu-thumb-primary' : ''}`}
                    onClick={() => setPrimary(idx)}
                    role="button"
                    title={isPrimary ? t('image_upload.primary_photo') : t('image_upload.set_as_primary')}
                  >
                    {src ? (
                      <img src={src} alt={f.name} />
                    ) : (
                      <div className="iu-thumb-fallback">
                        <PictureOutlined />
                      </div>
                    )}
                    {isPrimary ? (
                      <span className="iu-badge">
                        <StarFilled /> Primary
                      </span>
                    ) : (
                      <span className="iu-badge iu-badge-set">
                        <StarOutlined /> Set primary
                      </span>
                    )}
                    <button
                      type="button"
                      className="iu-remove"
                      onClick={e => {
                        e.stopPropagation();
                        removeAt(idx);
                      }}
                      aria-label="Remove photo"
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Form.Item>
    </Col>
  );
};
